/**
 * Immo 360 / Prospect 360 mobile section disclosure helpers.
 * Breakpoint matches Tailwind `md` (768px): mobile is below 768px.
 */

import {
  adminProspectAdditionalOwners,
  adminProspectPrimaryOwner,
  adminProspectStatusLabel,
} from './adminProspectDisplay.js';

export const PROSPECT_DETAIL_MOBILE_MQ = '(max-width: 767px)';

export const PROSPECT_DETAIL_SECTION_IDS = Object.freeze([
  'followUp',
  'building',
  'pipeline',
  'primaryContact',
  'owners',
  'importedProperty',
  'importHistory',
  'associatedCampaigns',
  'publicResponses',
  'ownerProvidedProperty',
  'ownerProvidedFinancial',
  'prospectContext',
]);

export const PROSPECT_DETAIL_DEFAULT_OPEN = Object.freeze({
  followUp: true,
  building: false,
  pipeline: false,
  primaryContact: false,
  owners: false,
  importedProperty: false,
  importHistory: false,
  associatedCampaigns: false,
  publicResponses: false,
  ownerProvidedProperty: false,
  ownerProvidedFinancial: false,
  prospectContext: false,
});

export function createProspectDetailSectionOpenState(overrides = null) {
  return { ...PROSPECT_DETAIL_DEFAULT_OPEN, ...(overrides && typeof overrides === 'object' ? overrides : {}) };
}

export function isProspectDetailMobileViewport(matchMediaApi = globalThis.matchMedia) {
  if (typeof matchMediaApi !== 'function') return false;
  try {
    return Boolean(matchMediaApi(PROSPECT_DETAIL_MOBILE_MQ)?.matches);
  } catch {
    return false;
  }
}

export function subscribeProspectDetailMobileViewport(onChange, matchMediaApi = globalThis.matchMedia) {
  if (typeof onChange !== 'function') return () => {};
  if (typeof matchMediaApi !== 'function') {
    onChange(false);
    return () => {};
  }
  let media;
  try {
    media = matchMediaApi(PROSPECT_DETAIL_MOBILE_MQ);
  } catch {
    onChange(false);
    return () => {};
  }
  if (!media) {
    onChange(false);
    return () => {};
  }
  const handler = () => onChange(Boolean(media.matches));
  handler();
  if (typeof media.addEventListener === 'function') {
    media.addEventListener('change', handler);
    return () => media.removeEventListener('change', handler);
  }
  if (typeof media.addListener === 'function') {
    media.addListener(handler);
    return () => media.removeListener(handler);
  }
  return () => {};
}

export function toggleProspectDetailSection(openState, sectionId, nextOpen) {
  const id = String(sectionId || '').trim();
  if (!id || !Object.prototype.hasOwnProperty.call(PROSPECT_DETAIL_DEFAULT_OPEN, id)) {
    return openState || createProspectDetailSectionOpenState();
  }
  const current = openState && typeof openState === 'object' ? openState : createProspectDetailSectionOpenState();
  const resolved = typeof nextOpen === 'boolean' ? nextOpen : !current[id];
  if (current[id] === resolved) return current;
  return { ...current, [id]: resolved };
}

export function ensureProspectDetailSectionOpen(openState, sectionId) {
  return toggleProspectDetailSection(openState, sectionId, true);
}

/**
 * Reveal a section for actionable attention (error / conflict / refresh failure).
 * Bound to a prospect+visit so stale async results cannot open sections on a new visit.
 */
export function requestProspectDetailSectionAttention(
  openState,
  {
    sectionId,
    ownerProspectId,
    requestVisitId,
    getActiveProspectId,
    getActiveVisitId,
    getMounted,
    reason = 'attention',
  } = {},
) {
  if (typeof getMounted === 'function' && !getMounted()) {
    return { ok: false, reason: 'unmounted', openState, didExpand: false };
  }
  const owner = String(ownerProspectId || '').trim();
  const active = String(typeof getActiveProspectId === 'function' ? getActiveProspectId() : '').trim();
  if (!owner || !active || owner !== active) {
    return { ok: false, reason: 'stale-prospect', openState, didExpand: false };
  }
  if (requestVisitId != null && typeof getActiveVisitId === 'function') {
    if (requestVisitId !== getActiveVisitId()) {
      return { ok: false, reason: 'stale-visit', openState, didExpand: false };
    }
  }
  const id = String(sectionId || '').trim();
  if (!id || !Object.prototype.hasOwnProperty.call(PROSPECT_DETAIL_DEFAULT_OPEN, id)) {
    return { ok: false, reason: 'invalid-section', openState, didExpand: false };
  }
  const current = openState && typeof openState === 'object' ? openState : createProspectDetailSectionOpenState();
  if (current[id]) {
    return { ok: true, reason, openState: current, didExpand: false, sectionId: id };
  }
  return {
    ok: true,
    reason,
    openState: ensureProspectDetailSectionOpen(current, id),
    didExpand: true,
    sectionId: id,
  };
}

/** Host element for section chrome — always the same tag to avoid remounts. */
export function prospectDetailSectionHostTag() {
  return 'section';
}

/**
 * Lazy panels (import history / campaigns): keep content mounted once loaded.
 * Visibility is controlled separately so desktop Hide does not wipe pagination.
 */
export function shouldMountProspectLazyPanel({
  mobileLayout = false,
  mobileActive = false,
  mobileDisclosedOnce = false,
  open = false,
  status = 'idle',
} = {}) {
  if (status && status !== 'idle') return true;
  if (mobileLayout) return Boolean(mobileActive || mobileDisclosedOnce);
  return Boolean(open);
}

/** Desktop uses the local open flag; mobile defers hide to the parent disclosure. */
export function isProspectLazyPanelVisible({
  mobileLayout = false,
  mobileActive = false,
  mobileDisclosedOnce = false,
  open = false,
  status = 'idle',
} = {}) {
  if (mobileLayout) {
    return shouldMountProspectLazyPanel({
      mobileLayout,
      mobileActive,
      mobileDisclosedOnce,
      open,
      status,
    });
  }
  return Boolean(open);
}

export function prospectDetailSectionPanelId(sectionId) {
  return `prospect-detail-section-${String(sectionId || '').trim()}`;
}

export function prospectDetailSectionButtonId(sectionId) {
  return `prospect-detail-section-toggle-${String(sectionId || '').trim()}`;
}

/** Compact summaries from already-loaded detail / timeline state — no extra reads. */
export function buildProspectDetailSectionSummary(sectionId, { detail, activities, timelineStatus, t } = {}) {
  const empty = (key) => (typeof t === 'function' ? t(key) : '');
  switch (sectionId) {
    case 'followUp': {
      if (timelineStatus === 'loading') return empty('admin.prospection.detail.sectionSummary.loading');
      if (timelineStatus === 'error') return empty('admin.prospection.detail.sectionSummary.error');
      const count = Array.isArray(activities) ? activities.length : 0;
      if (count === 0 || timelineStatus === 'empty') {
        return empty('admin.prospection.detail.sectionSummary.followUpEmpty');
      }
      return empty('admin.prospection.detail.sectionSummary.followUpCount').replace('{count}', String(count));
    }
    case 'building': {
      const units = detail?.numberOfUnits;
      if (units == null || units === '') return empty('admin.prospection.detail.sectionSummary.buildingEmpty');
      return empty('admin.prospection.detail.sectionSummary.buildingUnits').replace('{count}', String(units));
    }
    case 'pipeline': {
      const status = detail?.status;
      if (!status) return empty('admin.prospection.detail.sectionSummary.empty');
      const label = typeof t === 'function' ? adminProspectStatusLabel(status, t) : String(status);
      return empty('admin.prospection.detail.sectionSummary.pipelineStatus').replace('{status}', label);
    }
    case 'primaryContact': {
      const contact = detail?.primaryContact;
      if (!contact) return empty('admin.prospection.detail.sectionSummary.contactEmpty');
      const name = contact.fullName || contact.name || contact.email || contact.phone;
      if (!name) return empty('admin.prospection.detail.sectionSummary.contactEmpty');
      return empty('admin.prospection.detail.sectionSummary.contactPresent');
    }
    case 'owners': {
      const primary = adminProspectPrimaryOwner(detail);
      const additional = adminProspectAdditionalOwners(detail);
      if (!primary?.name && !additional.length) {
        return empty('admin.prospection.detail.sectionSummary.ownersEmpty');
      }
      return empty('admin.prospection.detail.sectionSummary.ownersPresent');
    }
    case 'importedProperty': {
      const has =
        detail?.latestImportedPropertyProfile ||
        detail?.latestMunicipalAssessment ||
        detail?.latestRegistryActivity ||
        (Array.isArray(detail?.lotNumbers) && detail.lotNumbers.length);
      return has
        ? empty('admin.prospection.detail.sectionSummary.importedPresent')
        : empty('admin.prospection.detail.sectionSummary.importedEmpty');
    }
    case 'importHistory':
      return empty('admin.prospection.detail.sectionSummary.importHistoryLazy');
    case 'associatedCampaigns':
      return empty('admin.prospection.detail.sectionSummary.campaignsLazy');
    case 'publicResponses': {
      const summary = detail?.publicResponseSummary;
      if (!summary) return empty('admin.prospection.detail.sectionSummary.responsesEmpty');
      const intents =
        summary.receivedIntents && typeof summary.receivedIntents === 'object'
          ? Object.values(summary.receivedIntents).filter((entry) => entry?.status === 'received')
          : [];
      if (intents.length > 0) {
        return empty('admin.prospection.detail.sectionSummary.responsesCount').replace(
          '{count}',
          String(intents.length),
        );
      }
      if (summary.lastRespondedAt || summary.firstRespondedAt) {
        return empty('admin.prospection.detail.sectionSummary.responsesPresent');
      }
      return empty('admin.prospection.detail.sectionSummary.responsesEmpty');
    }
    case 'ownerProvidedProperty': {
      return detail?.latestOwnerProvidedPropertyProfile
        ? empty('admin.prospection.detail.sectionSummary.ownerPropertyPresent')
        : empty('admin.prospection.detail.sectionSummary.ownerPropertyEmpty');
    }
    case 'ownerProvidedFinancial': {
      return detail?.latestOwnerProvidedFinancialSnapshot
        ? empty('admin.prospection.detail.sectionSummary.ownerFinancialPresent')
        : empty('admin.prospection.detail.sectionSummary.ownerFinancialEmpty');
    }
    case 'prospectContext': {
      const notes = typeof detail?.notes === 'string' ? detail.notes.trim() : '';
      return notes
        ? empty('admin.prospection.detail.sectionSummary.contextPresent')
        : empty('admin.prospection.detail.sectionSummary.contextEmpty');
    }
    default:
      return empty('admin.prospection.detail.sectionSummary.empty');
  }
}
